import { useAuthStore, useNavigationStore, useOrderStore, useTrackStore, useUserStore } from '~/store'
import * as Sentry from '@sentry/browser'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { getPostSignInRoute } from '~/composables/signIn'
import type { OnboardingQueries } from '~/types/onboard'
import type { LaunchLinkType } from '~/types/route'
import { getTrackingAttributionFromQuery } from '~/utils/trackingAttribution'
const { showJoinRelishModal } = useJoinRelishModal()

export async function middlewareDeepLinkMobile(
  toPath: RouteLocationNormalized['path'],
  launchLinkType: LaunchLinkType
): Promise<RouteLocationRaw> {
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const trackStore = useTrackStore()
  const navigationStore = useNavigationStore()
  const orderStore = useOrderStore()

  // reset the trackStore and navigation store
  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: `Resetting track and navigation store for deep link processing`,
    level: 'info',
    data: { toPath, launchLinkType }
  })
  navigationStore.$reset()
  trackStore.$reset()

  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: `Deep link navigation started: ${toPath}`,
    level: 'info',
    data: { launchLinkType, toPath }
  })

  // Extract query parameters
  const url = new URL(toPath, window.location.origin)
  const queryParams = Object.fromEntries(url.searchParams.entries())
  const attribution = getTrackingAttributionFromQuery(queryParams)
  if (attribution) {
    trackStore.setAttribution(attribution)
  }

  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: 'Extracted query parameters',
    level: 'debug',
    data: { queryParams }
  })

  // Remove query parameters from the path and process the slug
  const pathWithoutQuery = url.pathname

  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: `Path without query: ${pathWithoutQuery}`,
    level: 'debug'
  })

  if (attribution) {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Tracking attribution detected and replaced',
      level: 'info',
      data: {
        source: attribution.source,
        hasUtm: Boolean(attribution.source || attribution.medium || attribution.campaign),
        capturedAt: attribution.capturedAt
      }
    })
  }

  // Track app launch from link
  const linkSource = queryParams.link_source
  gaEventLaunchFromLink(launchLinkType, pathWithoutQuery, linkSource, queryParams.link_type)

  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: 'Launch from link event',
    level: 'info',
    data: { launchLinkType, pathWithoutQuery, linkSource, link_type: queryParams.link_type }
  })

  // track the abandoned cart
  if (queryParams.link_type === 'abandoned_cart') {
    const cartId = queryParams.cart_id
    const currentCartId = orderStore.currentCartId
    if (cartId && cartId === currentCartId) {
      orderStore.currentOrderAbandoned = true
      Sentry.addBreadcrumb({
        category: 'deeplink',
        message: 'Abandoned cart detected',
        level: 'info',
        data: { cartId }
      })
    }
  }

  // Track the sign up experience
  if (queryParams.gexp) {
    navigationStore.experienceTag = queryParams.gexp
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Sign up experience tag set',
      level: 'info',
      data: { gexp: queryParams.gexp }
    })
  }

  const slug = pathWithoutQuery
    .split(/\.dev|\.com\.au/)
    .pop()
    ?.split('/')
    .filter((item) => item !== '')

  Sentry.addBreadcrumb({
    category: 'deeplink',
    message: `Slug parsed from path: ${JSON.stringify(slug)}`,
    level: 'debug'
  })

  // We only push to the route if there is a slug present
  if (!slug) {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'No slug found, redirecting to onboard/home',
      level: 'warning',
      data: { hasOnboarded: userStore.hasOnboarded }
    })
    if (!userStore.hasOnboarded) {
      return { name: 'onboard' }
    } else {
      return { name: 'home' }
    }
  }

  // e.g slug = ['order-at-table', '180']
  if (slug[0] === 'order-at-table') {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Order at table deep link',
      level: 'info',
      data: { slug }
    })
    gaEventLaunchViaQRCode()
    const restaurantId = slug[1]

    if (!userStore.hasOnboarded) {
      if (authStore.isAuthenticated) {
        Sentry.addBreadcrumb({
          category: 'deeplink',
          message: 'User authenticated, redirecting to post sign-in route',
          level: 'info',
          data: { restaurantId }
        })
        const postSignInRoute = await getPostSignInRoute({ source: 'order-at-table', restaurantId }, queryParams)
        return postSignInRoute || { name: 'home' }
      }

      Sentry.addBreadcrumb({
        category: 'deeplink',
        message: 'User not onboarded, redirecting to onboard',
        level: 'info',
        data: { restaurantId }
      })
      return { name: 'onboard', query: { source: 'order-at-table', restaurantId, ...queryParams } }
    }
    // NOTE: The '/order-at-table' route validates the restaurant id
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Redirecting to order-at-table route',
      level: 'info',
      data: { restaurantId }
    })
    return { name: 'order-at-table', params: { restaurantId }, query: { ...queryParams } }
  } else if (slug[0] === 'applink') {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'App link deep link',
      level: 'info',
      data: { slug }
    })
    // If the user is not onboarded, redirect to the onboarding flow
    if (!userStore.hasOnboarded) {
      // special handling for applink/profile/referrals/code when not onboarded
      if (slug[1] === 'profile' && slug[2] === 'referrals' && slug[3]) {
        Sentry.addBreadcrumb({
          category: 'deeplink',
          message: 'Referral code detected in applink',
          level: 'info',
          data: { referralCode: slug[3] }
        })
        navigationStore.referralCode = slug[3].replace(/[^a-zA-Z0-9-]/g, '').substring(0, 20)
        navigationStore.pushFrom = '/home'
        navigationStore.pushTo = '/profile/referrals'
        navigationStore.isInitialisingNavigation.home = true
        const onboardingQueries: OnboardingQueries = {
          source: 'onboarding',
          referralCode: navigationStore.referralCode
        }
        showJoinRelishModal({ queries: onboardingQueries, showMadBundayPromo: toPath.includes('r=mad-bunday') })

        gaEventSignUpStart('applink_referral_code')
        return { name: 'onboard', query: { referralCode: slug[3], ...queryParams } }
      }
      Sentry.addBreadcrumb({
        category: 'deeplink',
        message: 'User not onboarded, redirecting to onboard (applink)',
        level: 'info',
        data: { slug }
      })
      return { name: 'onboard', query: { ...queryParams } }
    }
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Processing applink slugs',
      level: 'info',
      data: { slug }
    })
    const processedRoute = processAppLinkSlugs(slug, queryParams)
    navigationStore.$persist()
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Processed applink route',
      level: 'info',
      data: {
        processedRoute,
        navigationStore: navigationStore.$state
      }
    })
    return processedRoute
  }

  if (!userStore.hasOnboarded) {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'User not onboarded, redirecting to onboard (fallback)',
      level: 'info',
      data: { slug }
    })
    return { name: 'onboard', query: { ...queryParams } }
  } else {
    Sentry.addBreadcrumb({
      category: 'deeplink',
      message: 'Fallback: redirecting to home',
      level: 'info',
      data: { slug }
    })
    return { name: 'home', query: { ...queryParams } }
  }
}

export function processAppLinkSlugs(slug: string[], queryParams: Record<string, string>): RouteLocationRaw {
  const navigationStore = useNavigationStore()

  const redirectTarget = slug[1]
  switch (redirectTarget) {
    case 'home': {
      // e.g. /applink/home
      // Navigate straight to home route
      navigationStore.isInitialisingNavigation.home = true
      return { name: 'home', query: { ...queryParams } }
    }
    case 'menu': {
      // e.g. /applink/menu/product/123 or /applink/menu/category/123
      // Navigate to home and then push a product or category page
      navigationStore.pushFrom = '/home'
      const menuTargetType = slug[2]
      const id = slug[3]
      if (menuTargetType) {
        if (menuTargetType === 'product') {
          navigationStore.pushTo = 'menu-product'
          navigationStore.productId = parseInt(id)
        } else if (menuTargetType === 'category') {
          navigationStore.pushTo = '/menu'
          navigationStore.categoryId = parseInt(id)
        }
      }
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'config': {
      // e.g. /applink/config/restaurant/123/orderType/1
      // e.g. /applink/config/orderType/106/restaurant/180 or /applink/config/orderType/106
      // or /applink/config/restaurant/180 or /applink/config/restaurant/180/orderType/106
      navigationStore.pushTo = '/config'
      navigationStore.pushFrom = '/home'
      const processConfigSlugs = (key: string, value: string) => {
        if (key === 'restaurant') {
          navigationStore.temporaryRestaurantId = parseInt(value)
        } else if (key === 'orderType') {
          navigationStore.temporaryOrderType = parseInt(value)
        }
      }
      if (slug[2] && slug[3]) {
        processConfigSlugs(slug[2], slug[3])
      }
      if (slug[4] && slug[5]) {
        processConfigSlugs(slug[4], slug[5])
      }
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'history': {
      // e.g. /applink/history/123 or /applink/history
      const orderId = slug[2]
      navigationStore.pushFrom = '/home'
      navigationStore.pushTo = '/history'
      if (orderId) {
        navigationStore.orderId = parseInt(orderId)
      }
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'promo': {
      // e.g. /applink/promo/123
      navigationStore.modalFrom = '/home'
      navigationStore.modalTo = 'promo'
      const promoId = slug[2]
      if (promoId) {
        navigationStore.promoId = parseInt(promoId)
      }
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'sign-up': {
      // e.g. /applink/sign-up
      // Open sign-up modal from home
      navigationStore.isInitialisingNavigation.home = true
      navigationStore.modalFrom = '/home'
      navigationStore.modalTo = 'sign-up'
      return { path: '/home', query: { ...queryParams } }
    }
    case 'sign-in': {
      // e.g. /applink/sign-in
      // Open sign-in modal from home
      navigationStore.isInitialisingNavigation.home = true
      navigationStore.modalFrom = '/home'
      navigationStore.modalTo = 'sign-in'
      return { path: '/home', query: { ...queryParams } }
    }
    case 'relish': {
      // e.g. /applink/relish
      // Navigate to home and then push relish page
      navigationStore.pushTo = '/relish'
      navigationStore.pushFrom = '/home'
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'profile': {
      // e.g. /applink/profile
      // Navigate to home and then push profile page or supported subpage
      if (slug[2]) {
        // e.g. /applink/profile/mad-bunday
        const profileTarget = slug[2]
        if (profileTarget === 'mad-bunday') {
          navigationStore.pushTo = '/profile/mad-bunday'
        } else if (profileTarget === 'edit') {
          navigationStore.pushTo = '/profile/edit'
        } else if (profileTarget === 'burger-pass') {
          navigationStore.pushTo = '/profile/burger-pass'
        } else if (profileTarget === 'referrals') {
          navigationStore.pushTo = '/profile/referrals'
          const referralCode = slug.length > 3 ? slug[3].replace(/[^a-zA-Z0-9-]/g, '').substring(0, 20) : undefined
          navigationStore.referralCode = referralCode
        } else if (profileTarget === 'device-permissions') {
          navigationStore.pushTo = '/profile/device-permissions'
        }
      } else {
        navigationStore.pushTo = '/profile'
      }
      navigationStore.pushFrom = '/home'
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'student-sign-up': {
      // e.g. /applink/student-sign-up
      // Navigate to home and then push profile page
      navigationStore.pushTo = '/profile/edit/student-sign-up'
      navigationStore.pushFrom = '/home'
      navigationStore.isInitialisingNavigation.home = true
      return { path: '/home', query: { ...queryParams } }
    }
    case 'verification': {
      // e.g. /applink/verification
      // Navigate to home and then display verification modal
      navigationStore.isInitialisingNavigation.home = true
      navigationStore.modalFrom = '/home'
      navigationStore.modalTo = 'verification-modal'
      return { path: '/home', query: { ...queryParams } }
    }
    default:
      // Unsupported or unknown route
      // Navigate straight to home route
      navigationStore.isInitialisingNavigation.home = true
      return { name: 'home', query: { ...queryParams } }
  }
}
